import type {
  CardFieldElementType,
  PaypercutBillingDetails,
  PaypercutPaymentMethodFutureUsage,
} from '../types/elements';
import { generateId } from '../utils/generate-id';
import { CardCollectionRuntime, CardCollectionRuntimeError } from './card-collection-runtime';
import {
  ELEMENTS_COMPONENT_CHANNEL,
  ElementsControllerCommandType,
  type ElementsControllerCardCollectionTokenizationResultMessage,
  type ElementsSubmissionCancellationReason,
} from './controller-protocol';
import { createSubmitError, type PaymentMethodSubmissionSource } from './payment-method-submission';

export interface CardCollectionSubmissionContext {
  readonly controllerId: string;
  isDestroyed(): boolean;
  revealCardFieldSubmissionError(
    type: CardFieldElementType,
    errorType: 'incomplete' | 'invalid',
  ): void;
  submitCardCollection(
    componentId: string,
    requestId: string,
    deadlineAt: number,
    billingDetails?: PaypercutBillingDetails,
  ): void;
  cancelCardCollectionSubmission(
    componentId: string,
    requestId: string,
    reason?: ElementsSubmissionCancellationReason,
  ): void;
  attachCardCollectionActionChannel(
    componentId: string,
    requestId: string,
    actionId: string,
    port: MessagePort,
  ): void;
  sendCardCollectionTokenizationResult(
    message: ElementsControllerCardCollectionTokenizationResultMessage,
  ): void;
  onCardCollectionSubmissionStateChange(): void;
}

export class CardCollectionSubmissionSource implements PaymentMethodSubmissionSource {
  readonly submissionSourceId = generateId();
  private readonly tokenizations = new Map<
    string,
    Promise<ElementsControllerCardCollectionTokenizationResultMessage['result']>
  >();
  private destroyed = false;
  private activeRequestId: string | null = null;
  private activeFutureUsage: PaypercutPaymentMethodFutureUsage | undefined;
  private activeBillingDetails: PaypercutBillingDetails | undefined;
  private readonly canceledRequests = new Set<string>();

  constructor(
    private readonly runtime: CardCollectionRuntime,
    private readonly context: CardCollectionSubmissionContext,
  ) {}

  validatePaymentMethod(): Promise<void> {
    try {
      this.assertCanSubmitPaymentMethod();
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  assertCanSubmitPaymentMethod(): void {
    if (this.destroyed || this.context.isDestroyed()) {
      throw new CardCollectionRuntimeError({
        code: 'elements_destroyed',
        message: 'The split card fields have been destroyed.',
        recoverable: false,
      });
    }
    const issue = this.runtime.getSubmissionIssue();
    if (issue) this.context.revealCardFieldSubmissionError(issue.type, issue.errorType);
    try {
      this.runtime.assertCanSubmitPaymentMethod();
    } catch (error) {
      if (error instanceof CardCollectionRuntimeError) {
        throw createSubmitError(
          error.details.code,
          error.details.message,
          error.details.recoverable ?? true,
        );
      }
      throw error;
    }
  }

  beginPaymentMethodSubmission(
    requestId: string,
    deadlineAt: number,
    futureUsage?: PaypercutPaymentMethodFutureUsage,
    billingDetails?: PaypercutBillingDetails,
  ): void {
    this.activeRequestId = requestId;
    this.activeFutureUsage = futureUsage;
    this.activeBillingDetails = billingDetails;
    this.canceledRequests.delete(requestId);
    this.context.onCardCollectionSubmissionStateChange();
    if (billingDetails) {
      this.context.submitCardCollection(
        this.submissionSourceId,
        requestId,
        deadlineAt,
        billingDetails,
      );
    } else {
      this.context.submitCardCollection(this.submissionSourceId, requestId, deadlineAt);
    }
  }

  cancelPaymentMethodSubmission(
    requestId: string,
    reason: ElementsSubmissionCancellationReason,
  ): void {
    if (this.tokenizations.has(requestId)) this.canceledRequests.add(requestId);
    else this.canceledRequests.delete(requestId);
    if (this.activeRequestId === requestId) {
      this.activeRequestId = null;
      this.activeFutureUsage = undefined;
      this.activeBillingDetails = undefined;
    }
    this.tokenizations.delete(requestId);
    this.context.cancelCardCollectionSubmission(this.submissionSourceId, requestId, reason);
  }

  attachPaymentMethodActionChannel(requestId: string, actionId: string, port: MessagePort): void {
    this.context.attachCardCollectionActionChannel(
      this.submissionSourceId,
      requestId,
      actionId,
      port,
    );
  }

  onPaymentMethodSubmissionStateChange(): void {
    this.context.onCardCollectionSubmissionStateChange();
  }

  handleTokenizationRequest(
    requestId: string,
    futureUsage?: PaypercutPaymentMethodFutureUsage,
  ): void {
    if (
      this.destroyed ||
      this.activeRequestId !== requestId ||
      this.canceledRequests.has(requestId)
    ) {
      return;
    }
    if (this.activeFutureUsage !== futureUsage) return;
    if (this.tokenizations.has(requestId)) return;
    const tokenization = this.runtime
      .createConfirmationData(requestId, this.activeBillingDetails?.name, futureUsage)
      .then(
        (confirmationData) => ({ status: 'succeeded', confirmationData }) as const,
        (error: unknown) => {
          if (error instanceof CardCollectionRuntimeError) {
            return {
              status: 'failed',
              code: error.details.code,
              message: error.details.message,
              recoverable: error.details.recoverable ?? true,
            } as const;
          }
          return {
            status: 'failed',
            code: 'elements_payment_method_creation_failed',
            message: 'Payment details are temporarily unavailable. Please try again.',
            recoverable: true,
          } as const;
        },
      );
    this.tokenizations.set(requestId, tokenization);

    void tokenization.then((result) => {
      this.tokenizations.delete(requestId);
      if (this.destroyed || this.canceledRequests.delete(requestId)) {
        return;
      }
      if (this.activeRequestId !== requestId) return;
      this.activeRequestId = null;
      this.activeFutureUsage = undefined;
      this.activeBillingDetails = undefined;
      this.context.sendCardCollectionTokenizationResult({
        channel: ELEMENTS_COMPONENT_CHANNEL,
        type: ElementsControllerCommandType.CardCollectionTokenizationResult,
        controllerId: this.context.controllerId,
        componentId: this.submissionSourceId,
        componentType: 'card_collection',
        requestId,
        result,
      });
    });
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.activeRequestId = null;
    this.activeFutureUsage = undefined;
    this.activeBillingDetails = undefined;
    this.tokenizations.clear();
    this.canceledRequests.clear();
  }
}
